import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import './SchoolsTable.css';

function DistrictsTable({ districts, loading, onDistrictClick, onRefresh, refreshing }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [testFilter, setTestFilter] = useState('all'); // all | hide | only (internal test accounts)
  // Optimistic local overrides so the toggle flips instantly (no wait on the PATCH + refetch).
  const [internalOverrides, setInternalOverrides] = useState({});
  const isInternal = (d) => (d && d.id in internalOverrides ? internalOverrides[d.id] : !!(d && d.internal));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': 'Basic ' + btoa(`${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`)
  });

  // Flag/unflag a district as an internal test account, straight from the list. Instant UI;
  // the server call runs in the background and only reverts on failure.
  const toggleInternal = async (district, e) => {
    e.stopPropagation(); // don't open the district detail
    const next = !isInternal(district);
    setInternalOverrides(prev => ({ ...prev, [district.id]: next }));
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/districts/${district.id}/internal`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ internal: next })
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
    } catch (err) {
      console.error('Error toggling internal:', err);
      setInternalOverrides(prev => ({ ...prev, [district.id]: !next })); // revert
      alert('Failed to update the internal flag. Please try again.');
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'ID', accessor: 'id' },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row }) => <span>{row.original.name}</span>,
      },
      { Header: 'Schools', accessor: 'schoolCount' },
      { Header: 'Claimed orders', accessor: 'claimedOrderCount' },
      {
        Header: 'Account',
        accessor: 'internal',
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            className={`test-toggle ${isInternal(row.original) ? 'on' : ''}`}
            onClick={(e) => toggleInternal(row.original, e)}
            title={isInternal(row.original) ? 'Internal account. Click to mark as customer' : 'Customer account. Click to mark as internal'}
          >
            {isInternal(row.original) ? 'Internal' : 'Customer'}
          </button>
        ),
      },
    ],
    [internalOverrides]
  );

  const filteredDistricts = useMemo(() => {
    let rows = districts;
    if (testFilter === 'hide') rows = rows.filter(d => !isInternal(d));
    else if (testFilter === 'only') rows = rows.filter(d => isInternal(d));
    if (!debouncedSearchQuery) return rows;
    const query = debouncedSearchQuery.toLowerCase();
    return rows.filter(d => {
      const id = d.id ? d.id.toString().toLowerCase() : '';
      const name = d.name ? d.name.toLowerCase() : '';
      return id.includes(query) || name.includes(query);
    });
  }, [districts, debouncedSearchQuery, testFilter, internalOverrides]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: filteredDistricts,
      initialState: { sortBy: [{ id: 'name', desc: false }] },
    },
    useSortBy
  );

  return (
    <>
      <div className="content-header">
        <h2>Districts ({districts.length})</h2>
        <div className="list-controls">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <select
            className="test-filter-select"
            value={testFilter}
            onChange={(e) => setTestFilter(e.target.value)}
            title="Internal test accounts"
          >
            <option value="all">All accounts</option>
            <option value="hide">Hide internal</option>
            <option value="only">Only internal</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="no-data">Loading districts…</div>
      ) : districts.length === 0 ? (
        <div className="no-data">No districts found</div>
      ) : filteredDistricts.length === 0 ? (
        <div className="no-data">No districts match your search</div>
      ) : (
        <div className="table-container">
          <table {...getTableProps()} className="schools-table">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, j) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={j}>
                      <div className="th-content">
                        {column.render('Header')}
                        <span className="sort-indicator">
                          {column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    key={i}
                    onClick={() => onDistrictClick(row.original)}
                    className="clickable-row"
                  >
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default DistrictsTable;
